import yaml from 'js-yaml'
import { expandEditable } from './editable/expandEditable'
import { markDynamicRegions } from './editable/dynamicRegions'

/**
 * Parse a block definition payload. The backend stores definitions as raw
 * YAML text in `field_definition` to preserve comments and authoring style;
 * `js-yaml` accepts plain JSON as a valid YAML subset, so the same parser
 * handles legacy JSON blobs too.
 */
export const parseDefinition = (raw) => {
  if (raw == null) return {}
  if (typeof raw === 'object') return raw
  try { return yaml.load(raw) ?? {} }
  catch (err) {
    console.error('Builder: failed to parse block definition', err)
    return {}
  }
}

/**
 * Normalize a `builder_def` decoupled item into the flat registry shape
 * consumed by `BuilderBlockCatalog`, `BuilderCanvas` and le sélecteur de
 * modèles :
 *
 *   { type, name, category, description, defaultData, preview?,
 *     rjBuild, defNid }
 *
 * Extrait de `BuilderCanvas.jsx` parce que le sélecteur de modèles en a besoin
 * lui aussi (il monte les blocs d'une page pour l'aperçu), et que l'exporter
 * depuis un fichier qui exporte AUSSI un composant casserait le Fast Refresh de
 * Vite — `react-refresh/only-export-components`, dont le symptôme est un écran
 * vide.
 */
export const normalizeRegistryItem = (item) => {
  const d = item?.data ?? item ?? {}
  const def = parseDefinition(d.definition)
  return {
    type: d.type,
    name: d.name ?? d.type,
    category: def.category ?? '',
    description: def.description ?? '',
    // Vignette déclarée par la déf. Plus aucune vue ne s'en sert — le catalogue
    // monte le composant lui-même (BuilderBlockPreview), le seul aperçu qui
    // suive la déf sans maintenance. Conservée parce qu'elle voyage avec la
    // donnée et qu'aucune déf du dépôt n'en déclare : rien à migrer.
    preview: def.preview,
    // C1: `builder_def` carries a kind discriminator (block | object). Only
    // block defs are placeable in the canvas; object defs are filtered out
    // by `fetchRegistry()` below.
    kind: d.kind ?? 'block',
    // Site de rattachement (relation de groupe, pas un champ — ajouté au rendu
    // decoupled par orbit_site). NULL = déf de la bibliothèque partagée. C'est
    // l'ÉTIQUETTE, celle qu'affiche la pop-in d'informations.
    siteId: d.siteId ?? null,
    siteName: d.siteName ?? '',
    // TOUS les rattachements — une déf peut appartenir à plusieurs sites (les
    // blocs de landing sont mutualisés entre marques). C'est cette liste que le
    // catalogue lit pour cloisonner ; `siteId` n'en désigne qu'un, et comparer
    // dessus faisait disparaître la déf du catalogue des autres sites. Le repli
    // sur `siteId` couvre une réponse d'avant cette clé (cache du navigateur).
    siteIds: Array.isArray(d.siteIds)
      ? d.siteIds.map(Number)
      : (d.siteId ? [Number(d.siteId)] : []),
    // Droits sur la DÉFINITION, tels que Drupal les a livrés avec la donnée :
    // l'enveloppe Syncable ne porte `submission_url` / `delete_url` que si
    // l'utilisateur peut écrire / supprimer ce nœud (cf.
    // AccessAwareSyncableSubscriber). Le catalogue s'en sert pour n'offrir
    // « Modifier les informations » et « Supprimer » qu'à qui peut les faire —
    // aucune règle d'accès n'est rejouée côté front, et le serveur revérifie de
    // toute façon.
    canEdit: Boolean(item?.submission_url),
    canDelete: Boolean(item?.delete_url),
    defaultData: def.defaultData ?? {},
    // Clés de `defaultData` qu'un FILTRE PARTAGÉ de tableau de bord peut piloter
    // depuis l'URL (cf. `BuilderBlockMount` + bloc « Filtres partagés »). Liste
    // BLANCHE explicite : sans elle il faudrait rendre tout `defaultData`
    // adressable par l'URL, donc laisser n'importe quel lien réécrire le titre
    // ou la période d'un widget. Vide = le widget ignore les filtres partagés.
    sharedFilters: Array.isArray(def.sharedFilters) ? def.sharedFilters : [],
    // Réglages AVANCÉS du bloc : ce qui gouverne le bloc entier sans rien avoir
    // à cliquer dans le rendu (un thème, un mode anti-robot, la liste des
    // destinataires d'un formulaire). Ils vivent dans le MENU DU BLOC, replié
    // derrière un picto, et non dans un rail posé au milieu du contenu : un
    // réglage de bloc n'est pas une édition de contenu, et le rail se lisait
    // comme une pastille de plus à côté du formulaire. Chaque entrée a la forme
    // d'un `editable:` (`{field, type, label, icon?, …options}`) — même
    // vocabulaire que le `edit:` d'un `BuilderRowToolbar`, rien de neuf à
    // apprendre. Vide = pas de picto.
    settings: Array.isArray(def.settings) ? def.settings : [],
    // Expand inline `editable:` declarations into the `data-builder-*`
    // attributes the edit runtime reads. The build is the single schema source
    // (ADR-001) — no `fields[]` manifest on block defs anymore.
    // `markDynamicRegions` vient APRÈS `expandEditable` : il lit les
    // `data-builder-field` que celui-ci vient de poser, pour ne jamais voiler
    // une région qui contient encore quelque chose de modifiable.
    rjBuild: def.rjBuild ? markDynamicRegions(expandEditable(def.rjBuild)) : null,
    defNid: d.id,
    // Fingerprint of the stored definition (+ its catalogue name): lets a
    // registry reload tell an untouched entry from a genuinely modified one.
    signature: `${d.name ?? ''}\u0000${typeof d.definition === 'string' ? d.definition : JSON.stringify(d.definition ?? null)}`,
    version: 0,
  }
}

const DEF_COLLECTION = '/decoupled-api/node/builder_def/collection?display=decoupled'

/**
 * Les défs `builder_def`, normalisées et réduites aux défs POSABLES.
 *
 * `cache: 'no-store'` est porteur, pas une précaution : `services/requestCache`
 * enveloppe `window.fetch` d'un cache mémoire SANS TTL sur `/decoupled-api/`,
 * et n'honore que ce drapeau comme échappatoire. Sans lui, une déf réécrite
 * (par l'assistant, dans un autre onglet) reviendrait telle qu'elle était au
 * chargement de l'application.
 *
 * @param {string} [query] — conditions supplémentaires, déjà encodées.
 * @returns {Promise<Array|null>} les entrées, ou NULL si le fetch a échoué.
 */
const fetchDefs = async (query = '') => {
  try {
    const res = await fetch(`${DEF_COLLECTION}${query}`, { credentials: 'include', cache: 'no-store' })
    if (!res.ok) {
      console.error('Builder: registry fetch failed', res.status)
      return null
    }
    const items = await res.json()
    return (Array.isArray(items) ? items : [])
      .map(normalizeRegistryItem)
      // C1: the unified `builder_def` collection holds both block and object
      // defs; only block defs are placeable units in the canvas.
      .filter(e => e.type && e.kind === 'block')
  } catch (err) {
    console.error('Builder: registry reload error', err)
    return null
  }
}

/**
 * TOUT le catalogue, tous sites confondus (436 défs, 7,6 Mo le 2026-10-25).
 * Plus aucun écran ne le charge : le canevas lit les défs de SA page
 * (`fetchRegistryTypes`) et le catalogue « + » celles de SON site
 * (`fetchSiteCatalog`).
 */
export const fetchRegistry = () => fetchDefs()

/**
 * Les défs qu'offre le catalogue d'une page de ce site : celles du site et la
 * bibliothèque partagée (`siteScope`, cf. orbit_site DecoupledQuerySubscriber).
 * Sans site (`null`) : la bibliothèque partagée seule. Le tri fin (catégorie,
 * hôte) reste celui du catalogue, appliqué par-dessus.
 *
 * @param {number|string|null} siteId
 * @returns {Promise<Array|null>}
 */
export const fetchSiteCatalog = (siteId) =>
  fetchDefs(`&siteScope=${siteId ? encodeURIComponent(siteId) : 'none'}`)

// Une URL de filtre par type reste courte : au-delà, plusieurs requêtes en
// parallèle (une page de dashboard ou une comparaison de versions peut nommer
// des dizaines de types).
const TYPES_PER_REQUEST = 40

// Nom d'un type de bloc : PascalCase (`HeroBlock`, `OstrumFundHeader`).
const BLOCK_TYPE = /^[A-Z][A-Za-z0-9_]*$/

/**
 * Les types de blocs nommés n'importe où dans une page (ou un lot de pages) :
 * ses blocs, les cellules de ses gabarits (`data.cells`), l'unité qu'un bloc lié
 * embarque. Parcours profond plutôt qu'une liste des formes connues : un
 * conteneur de plus n'a rien à déclarer ici. Les faux positifs (une valeur
 * `type` de donnée qui ressemble à un nom de bloc) coûtent un élément d'URL et
 * ne reviennent pas, rien de plus.
 */
export const collectBlockTypes = (value) => {
  const found = new Set()
  const seen = new Set()
  const walk = (v) => {
    if (!v || typeof v !== 'object' || seen.has(v)) return
    seen.add(v)
    if (Array.isArray(v)) { v.forEach(walk); return }
    if (typeof v.type === 'string' && BLOCK_TYPE.test(v.type)) found.add(v.type)
    Object.values(v).forEach(walk)
  }
  walk(value)
  return [...found]
}

// Ce qu'une entrée FRAÎCHE devient dans le registre.
//
// Untouched def → keep the SAME object identity, so blocks already mounted from
// it aren't re-seeded (a unit block would lose pending edits). Modified def →
// new identity + a bumped `version`, which remounts exactly the blocks built
// from it.
export const mergeEntry = (old, entry) => (
  old && old.signature === entry.signature
    ? old
    : { ...entry, version: (old?.version ?? 0) + 1 }
)

/**
 * Fond des entrées fraîches dans le registre, EN PLACE : les connues sont
 * remplacées (identité gardée si inchangées), les nouvelles ajoutées. Le
 * registre ne contient que ce qui a été demandé (défs de la page, catalogue du
 * site) ; rien n'en sort ici. Rend `prev` lui-même quand rien n'a bougé : pas de
 * nouveau tableau, donc pas de re-rendu du canevas.
 */
export const mergeRegistry = (prev, fresh) => {
  if (!fresh?.length) return prev
  const byType = new Map(fresh.map(e => [e.type, e]))
  const next = prev.map(old => (
    byType.has(old.type) ? mergeEntry(old, byType.get(old.type)) : old
  ))
  const known = new Set(prev.map(e => e.type))
  for (const entry of fresh) {
    if (!known.has(entry.type)) {
      next.push(entry)
      known.add(entry.type)
    }
  }
  const changed = next.length !== prev.length || next.some((e, i) => e !== prev[i])
  return changed ? next : prev
}

/**
 * Les défs des SEULS types demandés.
 *
 * Ce que relit la fin d'un tour de l'assistant : il a touché un bloc, pas deux
 * cents. La collection entière pèse plusieurs mégaoctets et se relit sans cache
 * (voir ci-dessus) — la redemander à chaque tour rendait la main une seconde
 * plus tard, pour remplacer 205 entrées par elles-mêmes.
 *
 * Filtre `decoupled_toolbox` (cf. `FilterTrait`) : le nom du CHAMP Drupal, pas
 * la clé decoupled — le type d'un bloc est stocké dans `field_def_key`.
 *
 * @param {string[]} types
 * @returns {Promise<Array|null>} les entrées trouvées (possiblement moins que
 *   demandé : une déf supprimée ou hors droits n'en revient pas), ou NULL si le
 *   fetch a échoué.
 */
export const fetchRegistryTypes = async (types) => {
  const wanted = [...new Set((types ?? []).filter(t => typeof t === 'string' && t))]
  if (!wanted.length) return []
  const chunks = []
  for (let i = 0; i < wanted.length; i += TYPES_PER_REQUEST) chunks.push(wanted.slice(i, i + TYPES_PER_REQUEST))
  const results = await Promise.all(chunks.map((chunk) => {
    const values = chunk.map(t => `&filter[0][v][]=${encodeURIComponent(t)}`).join('')
    return fetchDefs(`&filter[0][f]=field_def_key${values}&filter[0][c]=IN`)
  }))
  return results.some(r => r === null) ? null : results.flat()
}
